/**
 * 看板几何常量 —— 严格对齐 MONDAY-CLONE-SPEC.md §3。
 *
 * 集中在这里是因为这些数字决定了渲染骨架（冻结区宽度、行高、组头高度、
 * 单元格高度公式），散落在 CSS 里改一处漏一处。
 */
export const GEO = {
  /** 数据行高：内容 35 + 1px 分隔线 */
  rowHeight: 36,
  /** 列头行高 */
  headerHeight: 36,
  /** 组名行高 */
  groupTitleHeight: 40,
  /** 组头总高 = 组名行 40 + 列头行 36 */
  groupHeaderHeight: 76,
  /** 折叠后的组头高 */
  groupHeaderCollapsedHeight: 74,
  /** 分组底部（含 36px 聚合行） */
  groupFooterHeight: 100,
  /** 左侧 gutter（行 ⋮ 在这里） */
  gutterWidth: 40,
  /** 组色条 */
  colorBarWidth: 6,
  /** 右侧指示条 */
  rightIndicatorWidth: 68,
  /** 冻结名称列总宽 */
  nameColumnWidth: 400,
} as const

/** 名称列内部分解（§4，校验 6 + 33 + 265 + 65 = 399） */
export const NAME_COLUMN_PARTS = {
  colorBar: 6,
  checkbox: 33,
  dragAndName: 265,
  conversation: 65,
} as const

/**
 * 当前生效的数据行高（px）。默认取 GEO.rowHeight，
 * 由 applyRowAndHeaderHeight 按看板显示设置（ViewState.ItemHeight）改写。
 */
let currentRowHeight: number = GEO.rowHeight

/**
 * 单元格高度公式（§3，源码实测）：
 *   lines <= 1 ? rowH-1 : 20*(lines-1) + rowH-1
 * 行高 36 时即 35 / 20*(lines-1)+35 —— 内容比行高少 1px 分隔线。
 * 行高可被显示设置改成 60，此时单行内容 = 59，多行每多一行加 20。
 */
export function cellHeight(lines: number, rowHeight: number = currentRowHeight): number {
  return lines <= 1 ? rowHeight - 1 : 20 * (lines - 1) + rowHeight - 1
}

/** 把 GEO 写进 CSS 变量，供 index.css 使用（单一数据源） */
export function applyGeometryVars(): void {
  const root = document.documentElement
  const vars: Record<string, string> = {
    '--geo-row-h': `${GEO.rowHeight}px`,
    '--geo-header-h': `${GEO.headerHeight}px`,
    '--geo-group-title-h': `${GEO.groupTitleHeight}px`,
    '--geo-group-header-h': `${GEO.groupHeaderHeight}px`,
    '--geo-group-header-collapsed-h': `${GEO.groupHeaderCollapsedHeight}px`,
    '--geo-group-footer-h': `${GEO.groupFooterHeight}px`,
    '--geo-gutter-w': `${GEO.gutterWidth}px`,
    '--geo-color-bar-w': `${GEO.colorBarWidth}px`,
    '--geo-right-indicator-w': `${GEO.rightIndicatorWidth}px`,
    '--geo-name-col-w': `${GEO.nameColumnWidth}px`,
  }
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v)
}

/**
 * 按看板显示设置覆盖行高与表头高（M8）。
 * 只改这两个变量：其它几何量（分组头、聚合条…）保持不变。
 * 传 0 或负数表示恢复默认。
 */
export function applyRowAndHeaderHeight(itemHeight: number, headerHeight: number): void {
  const root = document.documentElement
  const ih = itemHeight > 0 ? itemHeight : GEO.rowHeight
  const hh = headerHeight > 0 ? headerHeight : GEO.headerHeight
  currentRowHeight = ih
  // 供 CSS 按行高分支：行高 60 时文本单元格显示两行（见 index.css）
  root.dataset.rowHeight = String(ih)
  root.style.setProperty('--geo-row-h', `${ih}px`)
  root.style.setProperty('--geo-header-h', `${hh}px`)
}
/**
 * 冻结列数（M8）：名称列之后额外冻结几列。
 * 用模块级状态而非 props，是因为表格渲染在 GroupBlock 深处（同时要渲染 thead 的 th 与
 * 每行的 td），逐层透传会污染一长串 props；看板数据变化时整表会重渲染，取值即可刷新。
 */
let pinnedCount = 0

export function setPinnedCount(n: number): void {
  pinnedCount = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

export function getPinnedCount(): number {
  return pinnedCount
}